import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { FeaturedToy } from '../types/toy'
import { fetchFeaturedToys } from '../lib/featuredToys'
import './FeaturedToys.css'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; toys: FeaturedToy[] }
  | { kind: 'error'; message: string }

export default function FeaturedToys() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  useEffect(() => {
    const controller = new AbortController()

    fetchFeaturedToys({ signal: controller.signal })
      .then((toys) => setState({ kind: 'ready', toys }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Failed to load'
        setState({ kind: 'error', message })
      })

    return () => controller.abort()
  }, [])

  return (
    <section className="featured-toys">
      <h2>
        Featured toys.
      </h2>
      {state.kind === 'loading' && (
        <p className="featured-toys__status badge">
          <span className="bdot" />
          Loading featured toys…
        </p>
      )}
      {state.kind === 'error' && (
        <p
          className="featured-toys__status featured-toys__status--error"
          role="alert"
        >
          Couldn't load featured toys: {state.message}
        </p>
      )}
      {state.kind === 'ready' && state.toys.length === 0 && (
        <div className="card">
          <h3
            style={{
              fontFamily: 'var(--heading)',
              fontWeight: 700,
              margin: '0 0 8px',
              color: 'var(--ink)',
            }}
          >
            Nothing here yet.
          </h3>
          <p
            style={{
              fontFamily: 'var(--sans)',
              color: 'var(--ink-2)',
              margin: 0,
            }}
          >
            We'll drop new toys soon.
          </p>
        </div>
      )}
      {state.kind === 'ready' && state.toys.length > 0 && (
        <ul className="featured-toys__strip" aria-label="Featured toys">
          {state.toys.map((toy) => (
            <li key={toy.id} className="featured-toys__card">
              <Link to={`/toys/featured/${toy.id}`} className="featured-toys__link">
                {toy.thumbnailUrl ? (
                  <img
                    src={toy.thumbnailUrl}
                    alt={toy.title}
                    className="featured-toys__thumbnail"
                  />
                ) : (
                  <div className="featured-toys__art" aria-hidden="true">
                    {toy.title.charAt(0)}
                  </div>
                )}
                <h3 className="featured-toys__title">{toy.title}</h3>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
